import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-[3px] focus-visible:ring-[#F37020]/30",
  {
    variants: {
      variant: {
        default: "bg-[#1C1C32] text-white border border-[#2A2A45] hover:bg-[#222240]",
        destructive: "bg-[#FF4757] text-white hover:bg-[#FF4757]/90 focus-visible:ring-[#FF4757]/30",
        outline: "border border-[#2A2A45] bg-transparent text-white hover:bg-[#1C1C32] hover:text-white",
        secondary: "bg-[#FFD700] text-[#121220] hover:bg-[#FFD700]/80",
        ghost: "text-white hover:bg-[#1C1C32] hover:text-white",
        link: "text-[#F37020] underline-offset-4 hover:underline",
        primary: "bg-gradient-to-r from-[#F37020] to-[#FF8C42] text-white shadow-[0_0_20px_rgba(243,112,32,0.3)] hover:shadow-[0_0_25px_rgba(243,112,32,0.4)] hover:brightness-110",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";

  return <Comp data-slot="button" className={cn(buttonVariants({ variant, size, className }))} {...props} />;
}

export { Button, buttonVariants };
